import { fileIconStyles } from '@/styles/file-icon-styles';
import { useDraggable } from '@dnd-kit/core';
import { Button, Card, Flex, Spin, Typography } from 'antd';
import { getSession } from 'next-auth/react';
import NImage from 'next/image';
import { useContext, useEffect, useState } from 'react';
import { FileIcon } from 'react-file-icon';
import FileContextMenu from './FileContextMenu';
import FilePreviewModal from './FilePreviewModal';
import { LibraryContext } from './context-provider';
import styles from './styles.module.css';
const { Text } = Typography;

export default function FileCard({ file }) {
   const [filePreview, setFilePreview] = useState(null);
   const [loading, setLoading] = useState(false);
   const [openFilePreview, setOpenFilePreview] = useState(false);
   const [permission, setPermission] = useState(null);
   const { library, selectedLibrary, setSelectedLibrary } = useContext(LibraryContext);
   const { attributes, listeners, setNodeRef, transition, isDragging } = useDraggable({
      id: file._id,
      type: 'file',
      data: { type: 'file', ...file },
   });

   const dragStyle = {
      transition,
      opacity: isDragging ? 0.5 : 1,
   };

   useEffect(() => {
      setLoading(true);
      generateFilePreview(file);
      if (library?.data?.permission) setPermission(library?.data?.permission);
      else getPermission(file).then((permission) => setPermission(permission));
      setLoading(false);
   }, [file]);

   async function generateFilePreview(file) {
      const previewImage = new Image();
      previewImage.src = file?.file?.preview || file.file?.url;
      // try to load image
      previewImage.onload = () => {
         setFilePreview(previewImage.src);
      };
      // if image not loaded successfully, set filePreview to null
      previewImage.onerror = () => {
         setFilePreview(null);
      };
   }

   async function getPermission(file) {
      // get current user id
      const user = (await getSession()).user;

      // check if file creator is current user
      if (file.creator === user?.id) return 'write';

      // check if file is shared with current user
      const sharedWith = file.sharedWith?.find((item) => item.email === user?.email);
      if (sharedWith) return sharedWith.permission;

      return false;
   }

   function handleOnClick() {
      // if ctrl key or command key is pressed, add file to selectedLibrary
      if (window.event.ctrlKey || window.event.metaKey) {
         // if file is already selected, remove it from selectedLibrary
         if (selectedLibrary?.find((item) => item._id === file._id)) {
            setSelectedLibrary(selectedLibrary?.filter((item) => item._id !== file._id));
         } else {
            // add type property to file and add it to selectedLibrary
            setSelectedLibrary([...selectedLibrary, { type: 'file', ...file }]);
         }
      }

      // if ctrl key or command key is not pressed, set selectedLibrary to file
      else {
         setSelectedLibrary([{ type: 'file', ...file }]);
      }
   }

   function handleDoubleClick() {
      setOpenFilePreview(true);
   }

   return (
      <>
         <FilePreviewModal open={openFilePreview} setOpen={setOpenFilePreview} file={file} />
         <FileContextMenu trigger={['contextMenu']} permission={permission}>
            <Card
               styles={{ body: { padding: 8 } }}
               style={{ ...dragStyle }}
               {...attributes}
               {...listeners}
               ref={setNodeRef}
               onClickCapture={handleOnClick}
               onDoubleClick={handleDoubleClick}
               onContextMenu={() => setSelectedLibrary([{ type: 'file', ...file }])}
               className={`${styles.fileCard} ${
                  selectedLibrary?.find((item) => item._id === file._id) && styles.selected
               }`}
            >
               <Spin spinning={loading}>
                  <div className={styles.fileCardCover}>
                     <div className={styles.fileCardCoverOverlay}>
                        <FileContextMenu trigger={['click']} permission={permission}>
                           <Button icon={<i className="fi fi-rr-menu-dots-vertical" />} />
                        </FileContextMenu>
                     </div>

                     {filePreview ? (
                        <NImage
                           src={filePreview}
                           alt={file.name}
                           width={200}
                           height={200}
                           style={{ objectFit: 'contain', width: '100%', height: 'auto' }}
                        />
                     ) : (
                        <Flex
                           vertical
                           gap={8}
                           style={{ marginTop: 8 }}
                           align={'center'}
                           justify={'center'}
                        >
                           <div style={{ width: '25%' }}>
                              <FileIcon
                                 extension={file.file?.type?.split('/')[1]}
                                 {...fileIconStyles[file.file?.type?.split('/')[1]]}
                              />
                           </div>
                        </Flex>
                     )}
                  </div>
               </Spin>

               <Flex vertical gap={8} style={{ marginTop: 8 }}>
                  <Text strong>{file.name}</Text>
               </Flex>
            </Card>
         </FileContextMenu>
      </>
   );
}
